import React, { useState, useEffect } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { getSecurityLogs } from '../../services/rbacService';
import { Shield, Search, RefreshCw, LogIn, LogOut, AlertTriangle, Key, UserPlus, Trash2, UserCog } from 'lucide-react';

const EVENT_LABELS = {
  login_success: { label: 'Connexion réussie', icon: LogIn, color: 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400' },
  login_failed: { label: 'Échec connexion', icon: AlertTriangle, color: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400' },
  logout: { label: 'Déconnexion', icon: LogOut, color: 'text-gray-600 bg-gray-50 dark:bg-slate-700 dark:text-gray-400' },
  password_changed: { label: 'Mot de passe modifié', icon: Key, color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400' },
  user_created: { label: 'Utilisateur créé', icon: UserPlus, color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400' },
  user_deleted: { label: 'Utilisateur supprimé', icon: Trash2, color: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400' },
  role_changed: { label: 'Rôle modifié', icon: UserCog, color: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400' },
  user_activated: { label: 'Utilisateur activé', icon: UserPlus, color: 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400' },
  user_deactivated: { label: 'Utilisateur désactivé', icon: AlertTriangle, color: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400' },
};

export default function SecurityJournal() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEvent, setFilterEvent] = useState('all');

  const loadLogs = async () => {
    setLoading(true);
    const { data, error } = await getSecurityLogs({ limit: 200 });
    if (!error && data) setLogs(data);
    setLoading(false);
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const filteredLogs = logs?.filter((log) => {
    const matchSearch = !searchQuery ||
      log?.user_email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      log?.event_type?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      log?.ip_address?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchEvent = filterEvent === 'all' || log?.event_type === filterEvent;
    return matchSearch && matchEvent;
  });

  const stats = {
    total: logs?.length,
    logins: logs?.filter((l) => l?.event_type === 'login_success')?.length,
    failures: logs?.filter((l) => l?.event_type === 'login_failed')?.length,
    changes: logs?.filter((l) => ['role_changed', 'user_created', 'user_deleted']?.includes(l?.event_type))?.length,
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <span>Administration</span>
              <span>›</span>
              <span className="text-gray-900 dark:text-white font-medium">Journal de Sécurité</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Shield className="h-6 w-6 text-red-600" />
              Journal de Sécurité
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Historique des événements de sécurité du système
            </p>
          </div>
          <button
            onClick={loadLogs}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Actualiser
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total événements', value: stats?.total, color: 'text-gray-900 dark:text-white' },
            { label: 'Connexions réussies', value: stats?.logins, color: 'text-green-600 dark:text-green-400' },
            { label: 'Échecs connexion', value: stats?.failures, color: 'text-red-600 dark:text-red-400' },
            { label: 'Modifications', value: stats?.changes, color: 'text-orange-600 dark:text-orange-400' },
          ]?.map((stat) => (
            <div key={stat?.label} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{stat?.label}</p>
              <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par email, IP, événement..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
            />
          </div>
          <select
            value={filterEvent}
            onChange={(e) => setFilterEvent(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 outline-none"
          >
            <option value="all">Tous les événements</option>
            {Object.entries(EVENT_LABELS)?.map(([key, val]) => (
              <option key={key} value={key}>{val?.label}</option>
            ))}
          </select>
        </div>

        {/* Logs Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : filteredLogs?.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">Aucun événement trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Événement</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Utilisateur</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date & Heure</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Adresse IP</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Navigateur</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Détails</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {filteredLogs?.map((log) => {
                    const eventInfo = EVENT_LABELS?.[log?.event_type] || { label: log?.event_type, icon: Shield, color: 'text-gray-600 bg-gray-50' };
                    const EventIcon = eventInfo?.icon;
                    return (
                      <tr key={log?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${eventInfo?.color}`}>
                            <EventIcon className="h-3.5 w-3.5" />
                            {eventInfo?.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                          {log?.user_email || '—'}
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {log?.created_at ? new Date(log?.created_at)?.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' }) : '—'}
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {log?.ip_address || '—'}
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400 max-w-[200px] truncate" title={log?.user_agent}>
                          {log?.user_agent ? log?.user_agent?.substring(0, 40) + '...' : '—'}
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">
                          {log?.details && Object.keys(log?.details)?.length > 0 ? (
                            <span className="font-mono bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-xs">
                              {JSON.stringify(log?.details)?.substring(0, 60)}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
